import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { formatPrice, getVenueBenefits } from "../lib/api";
import type { Tier } from "../lib/types";

// Ported from src/app/venue/membership/subscription-disclosure-modal.tsx.
// Required disclosure shown before kicking the user out to Stripe Checkout,
// renders tier price + benefits, billing terms, cancellation policy. User
// must tick the agreement before "Continue to Payment" enables.
type Props = {
  tier: Tier;
  venueId: string;
  isOpen: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function SubscriptionDisclosureModal({
  tier,
  venueId,
  isOpen,
  loading,
  onClose,
  onConfirm,
}: Props) {
  const [agreed, setAgreed] = useState(false);

  // Reset on open
  useEffect(() => {
    if (isOpen) setAgreed(false);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !loading) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, loading, onClose]);

  const benefitsQuery = useQuery({
    queryKey: ["benefits", venueId],
    queryFn: () => getVenueBenefits(venueId),
    enabled: isOpen,
  });
  const tierBenefits = (benefitsQuery.data ?? []).filter(
    (b) => b.tier.id === tier.id,
  );

  if (!isOpen) return null;

  const intervalText = tier.interval === "ANNUAL" ? "year" : "month";
  const intervalPlural = tier.interval === "ANNUAL" ? "yearly" : "monthly";
  const priceFormatted = formatPrice(tier.price, tier.currency);

  const overlay = (
    <div className="modal-overlay" onClick={loading ? undefined : onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2>Subscription terms</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="modal-body">
          <section className="modal-plan">
            <div className="modal-plan-name">{tier.name}</div>
            <div className="modal-plan-price">
              <strong>{priceFormatted}</strong>
              <span className="per">/{intervalText}</span>
            </div>
            {tier.trialDays ? (
              <p className="modal-trial">
                Includes {tier.trialDays}-day free trial
              </p>
            ) : null}
            {tier.minCommitmentMonths ? (
              <p className="modal-trial">
                Minimum {tier.minCommitmentMonths}-month commitment
              </p>
            ) : null}
          </section>

          <section>
            <h3 className="modal-section-title">
              What you'll receive each {intervalText}:
            </h3>
            {benefitsQuery.isLoading ? (
              <p className="modal-muted">Loading benefits…</p>
            ) : tierBenefits.length === 0 ? (
              <p className="modal-muted">
                Tier benefits not yet configured. Talk to the venue.
              </p>
            ) : (
              <ul className="modal-benefits">
                {tierBenefits.map((b) => (
                  <li key={b.id}>
                    <span className="modal-check">✓</span>
                    <span>{b.description ?? b.type}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="modal-section-title">Billing:</h3>
            <ul className="modal-list">
              {tier.trialDays ? (
                <>
                  <li>
                    Your free trial starts today and lasts {tier.trialDays}{" "}
                    days.
                  </li>
                  <li>
                    After the trial, you'll be charged {priceFormatted}{" "}
                    {intervalPlural}.
                  </li>
                  <li>
                    Subscription auto-renews every {intervalText} unless
                    cancelled.
                  </li>
                </>
              ) : (
                <>
                  <li>You'll be charged {priceFormatted} today.</li>
                  <li>
                    Subscription auto-renews every {intervalText} at{" "}
                    {priceFormatted}.
                  </li>
                </>
              )}
              <li>Payments are processed by Stripe.</li>
            </ul>
          </section>

          <section>
            <h3 className="modal-section-title">How to cancel:</h3>
            <p className="modal-muted">
              Open your Account page, tap "Manage Subscription", then "Cancel".
              If you cancel during a trial period, you won't be charged. If you
              cancel after being charged, you'll keep access until the end of
              the current billing period.
            </p>
          </section>

          <section className="modal-stripe-note">
            By subscribing, you agree to our{" "}
            <a
              href="https://www.assemblytickets.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            . Your payment details are processed by Stripe and never touch our
            servers.
          </section>

          <label className="modal-agree">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={loading}
            />
            <span>
              I have read and agree to the subscription terms, billing
              information, and cancellation policy above.
            </span>
          </label>
        </div>

        <footer className="modal-footer">
          <button
            type="button"
            className="modal-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="subscribe-btn"
            onClick={onConfirm}
            disabled={!agreed || loading}
          >
            {loading ? "Redirecting…" : "Continue to payment"}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(
    overlay,
    document.getElementById("modal-root") ?? document.body,
  );
}
